/**
 * @file NewAsset.js
 * @description This screen allows user to create an asset from scratch.
 */

import { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import Toast from '../ui/toasts/Toast';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import { useAuthContext, useUploadContext } from '../../store/Contexts';
import { FileDrop } from '../helpers/FileDrop';
import TagInput from '../helpers/TagInput';
import { SearchableDropdown } from '../helpers/SearchableDropdown';
import Spinner from '../ui/spinners/Spinner';
import Tooltip from '../ui/tooltips/Tooltip';
import {
    InputField,
    TextAreaField,
    SwitchField,
    CheckboxField,
} from '../helpers/FormComponents';
import {
    formatFileSize,
    getFileCategory,
    getVideoRuntime,
    getAudioLength,
    getImageDimensions,
    sanitizeFileName,
} from '../helpers/FileUtils';
import { searchIndividuals } from '../../services/individualService';
import { fetchPopulationGroups } from '../../services/populationGroupService';
import { getClientOptions } from '../../services/clientService';
import { submitNewAsset } from '../../services/assetLibraryService';
import styles from './AssetDrawer.module.scss';
import '../page-list/PageList.css';
import { isValidFileTypeAndSize } from '../../utils/validate';
import {
    ClientOptionEnum,
    ResourceScope,
    SecurityAttributes,
} from '../../utils/data/enums';
import { getText } from '../../i18n';
import RichTextEditor from '../new-portal-page/RichTextEditor';

const NewAsset = ({ viewMode = null }) => {
    const location = useLocation();
    const isResourceLibrary =
        viewMode === 'ResourceLibrary' ||
        location.pathname.includes('/resources');
    const [file, setFile] = useState();
    const [asset, setAsset] = useState({
        assetTypeId: isResourceLibrary ? 6 : 1,
    });
    const [individualsDropdownArray, setIndividualsDropdownArray] = useState(
        [],
    );
    const [popGroupsDropdownArray, setPopGroupsDropdownArray] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [allowAssetSharing, setAllowAssetSharing] = useState(false);
    const [activeResourceTab, setActiveResourceTab] = useState('general');
    const navigate = useNavigate();
    const { showModal } = useContext(ModalContext);
    const { user, hasSecurityAttribute } = useAuthContext();
    const isResourceLibraryReadOnly =
        hasSecurityAttribute([SecurityAttributes.ResourceLibraryReadOnly]) &&
        !hasSecurityAttribute([SecurityAttributes.ResourceLibraryFullAccess]);
    const isAssetLibraryReadOnly = isResourceLibrary
        ? isResourceLibraryReadOnly
        : hasSecurityAttribute([SecurityAttributes.AssetLibraryReadOnly]);
    const { enqueueUpload } = useUploadContext();
    const isMyAssets = location.pathname.includes('my-assets');
    const isGlobalAssets = location.pathname.includes('global-assets');
    const selectedFileCategory = getFileCategory(file?.name || '');
    const isResourceTranscriptRelevant =
        selectedFileCategory === 'video' || selectedFileCategory === 'audio';

    const resourceScopeOptions = Object.entries(ResourceScope).map(
        ([scopeName, scope]) => ({
            id: scope,
            name: scopeName === 'ClientAdmin' ? 'Client Admin' : scopeName,
            className: styles[`resourceScope${scopeName}`],
        }),
    );
    const selectedResourceScopes = resourceScopeOptions.filter(
        scope => ((asset.resourceScopeMask || 0) & scope.id) === scope.id,
    );

    const updateResourceScopes = scopes => {
        const resourceScopeMask = scopes.reduce(
            (mask, scope) => mask | scope.id,
            0,
        );
        setAsset(prev => ({ ...prev, resourceScopeMask }));
    };

    const handleFileChange = fileValue => {
        if (asset.url) {
            setAsset(prev => ({
                ...prev,
                url: null,
            }));
        }
        setFile(sanitizeFileName(fileValue));
    };

    useEffect(() => {
        if (isResourceLibrary) {
            return;
        }

        searchIndividuals(null).then(response => {
            const formattedIndividuals = response.individuals.map(
                individual => ({
                    id: individual.id,
                    name: `${individual.firstName} ${individual.lastName}`,
                }),
            );
            setIndividualsDropdownArray(formattedIndividuals);
        });

        fetchPopulationGroups(null).then(response => {
            setPopGroupsDropdownArray(response);
        });

        if (isMyAssets) {
            getClientOptions(0, [ClientOptionEnum.AllowAssetSharing])
                .then(data => {
                    const option = data.find(
                        item =>
                            item.clientOptionId ===
                            ClientOptionEnum.AllowAssetSharing,
                    );
                    if (option) {
                        setAllowAssetSharing(option.value !== 'false');
                    }
                })
                .catch(error =>
                    console.error(
                        'Failed to fetch asset sharing option',
                        error,
                    ),
                );
        }
    }, []);

    const navigateBack = () => {
        let relUrl = `/assets`;
        if (isMyAssets) {
            relUrl = `/my-assets`;
        } else if (isGlobalAssets) {
            relUrl = `/global-assets`;
        } else if (isResourceLibrary) {
            relUrl = `/resources`;
        }

        navigate(relUrl);
    };

    const handleClose = () => {
        const isFormPartiallyCompleted =
            asset.name ||
            asset.description ||
            asset.assetTags > 0 ||
            (asset.assetIndividuals && asset.assetIndividuals.length > 0) ||
            (asset.assetGroups && asset.assetGroups.length > 0) ||
            asset.isLocked ||
            asset.assetTypeId === 1 ||
            asset.overview ||
            asset.transcript ||
            asset.resourceScopeMask ||
            asset.isHidden;

        if (isFormPartiallyCompleted) {
            showModal({
                type: ModalTypes.ACTION,
                title: getText('msg_b78130c92f32'),
                subtitle: getText('msg_27c483fb024c'),
                text: getText('msg_56b062b4a856'),
                onYes: () => {
                    navigateBack();
                },
                onNo: () => {
                    return;
                },
                yesButtonText: getText('msg_36fff63ccbcd'),
                noButtonText: getText('msg_77dfd2135f4d'),
            });
        } else {
            navigateBack();
        }
    };

    function updateAsset(asset, key, value) {
        return { ...asset, [key]: value };
    }

    const handleOverviewContentUpdate = useCallback(content => {
        setAsset(previousAsset => ({ ...previousAsset, overview: content }));
    }, []);

    const handleTranscriptContentUpdate = useCallback(content => {
        setAsset(previousAsset => ({ ...previousAsset, transcript: content }));
    }, []);

    const validateForm = () => {
        if (!asset.name || asset.name.trim() === '') {
            Toast.error(getText('msg_d7659e2c4af2'));
            return false;
        }

        if (!file) {
            Toast.error(getText('msg_e11c5988bd25'));
            return false;
        }

        const fileTypeAndSizeValidation = isValidFileTypeAndSize(file);
        if (fileTypeAndSizeValidation) {
            Toast.error(fileTypeAndSizeValidation);
            return false;
        }

        if (asset.name.length > 256) {
            Toast.error(getText('msg_f4231db74e51'));
            return false;
        }

        if (asset.description && asset.description.length > 200) {
            Toast.error(getText('msg_04bc9f18737a'));
            return false;
        }

        if (
            isResourceLibrary &&
            (!asset.resourceScopeMask || asset.resourceScopeMask <= 0)
        ) {
            Toast.error(getText('msg_87b6d1d44d3e'));
            return false;
        }

        return true;
    };

    const uploadAsset = async () => {
        setIsSaving(true);

        if (!validateForm()) {
            setIsSaving(false);
            return;
        }

        try {
            const newAsset = { ...asset };
            newAsset.assetGroups =
                asset.assetGroups && asset.assetGroups.length > 0
                    ? asset.assetGroups.map(group => group.id.toString())
                    : [];
            newAsset.assetIndividuals =
                asset.assetIndividuals && asset.assetIndividuals.length > 0
                    ? asset.assetIndividuals.map(individual =>
                          individual.id.toString(),
                      )
                    : [];
            newAsset.assetTypeId = isResourceLibrary
                ? 6
                : isMyAssets
                  ? 3
                  : newAsset.assetTypeId || 2;
            if (isResourceLibrary) {
                newAsset.agencyId = user?.jwt?.agencies?.includes(0)
                    ? 0
                    : user?.jwt?.agencies?.[0];
            } else if (isGlobalAssets) {
                newAsset.agencyId = 0;
            }
            newAsset.fileType = file.name.split('.').pop();
            newAsset.fileSize = formatFileSize(file.size);

            const fileCategory = getFileCategory(file.name);
            if (fileCategory === 'video') {
                newAsset.metaData = await getVideoRuntime(file);
            } else if (fileCategory === 'audio') {
                newAsset.metaData = await getAudioLength(file);
            } else if (fileCategory === 'image') {
                newAsset.metaData = await getImageDimensions(file);
            }

            const hasExistingUrl = !!asset.url;

            //Uploads and creates the asset in the background so the screen stays usable
            enqueueUpload(file.name, uploadOptions =>
                submitNewAsset({
                    file,
                    newAsset,
                    hasExistingUrl,
                    uploadOptions,
                }),
            );

            // Notify the user that the asset has been queued for upload, go back to list screen
            Toast.info(getText('msg_9309271e92ff'));
            navigateBack();
        } catch (error) {
            console.error('An error occurred:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 786);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 786);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const getSubtitle = () => {
        let str = getText('msg_d096d8ce698b');
        if (isResourceLibrary) {
            return getText('msg_4c2d2d6b7ee9');
        }
        if (isMyAssets) {
            str = getText('msg_39a38f4a4709');
        } else if (isGlobalAssets) {
            str = getText('msg_19d9d5bf7102');
        }
        return str;
    };

    return (
        <div className={`page-content ${styles.newAssetPage}`}>
            {isSaving && (
                <div className={styles.savingOverlay}>
                    <Spinner width={50} className={styles.loadingSpinner} />
                </div>
            )}
            <div className={`card ${styles.newAssetCard}`}>
                <div className="card-body px-0 py-4">
                    <div className="d-flex justify-content-between align-items-start">
                        <div className="d-flex flex-column ms-4">
                            <p className="page-title card-title mb-0">
                                {getText('msg_08b99c5d3e8f')}
                            </p>
                            <p
                                className={`ing-assets-subtitle mt-1 ${styles.subtitleHeight30}`}
                            >
                                {getSubtitle()}
                            </p>
                        </div>
                        <div className="float-end d-flex align-items-center me-4">
                            <p
                                type="button"
                                onClick={handleClose}
                                className="ing-text-buttons d-inline ms-3"
                            >
                                <i className="bi bi-x me-2" />
                                {getText('msg_bbfa773e5a63')}
                            </p>
                        </div>
                    </div>

                    <hr className={styles.hrDividerWhite} />
                    <div className="ing-new-assets-container">
                        {isResourceLibrary && (
                            <div className={styles.resourceTabs} role="tablist">
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={
                                        activeResourceTab === 'general'
                                    }
                                    className={`${styles.resourceTab} ${activeResourceTab === 'general' ? styles.resourceTabActive : ''}`}
                                    onClick={() =>
                                        setActiveResourceTab('general')
                                    }
                                >
                                    {getText('msg_15d1a2c5a0f9')}
                                </button>
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={
                                        activeResourceTab === 'overview'
                                    }
                                    className={`${styles.resourceTab} ${activeResourceTab === 'overview' ? styles.resourceTabActive : ''}`}
                                    onClick={() =>
                                        setActiveResourceTab('overview')
                                    }
                                >
                                    {getText('msg_0efc2e6be4c2')}
                                </button>
                                {isResourceTranscriptRelevant && (
                                    <button
                                        type="button"
                                        role="tab"
                                        aria-selected={
                                            activeResourceTab === 'transcript'
                                        }
                                        className={`${styles.resourceTab} ${activeResourceTab === 'transcript' ? styles.resourceTabActive : ''}`}
                                        onClick={() =>
                                            setActiveResourceTab('transcript')
                                        }
                                    >
                                        {getText('msg_5bcd602254f3')}
                                    </button>
                                )}
                            </div>
                        )}

                        {(!isResourceLibrary ||
                            activeResourceTab === 'general') && (
                            <>
                                <div className={styles.fileDropSpacing}>
                                    <FileDrop
                                        file={file}
                                        onChange={handleFileChange}
                                    />
                                </div>

                                <InputField
                                    label={getText('msg_709a23220f2c')}
                                    onChange={value =>
                                        setAsset(
                                            updateAsset(asset, 'name', value),
                                        )
                                    }
                                    maxChars={256}
                                    required={true}
                                    placeholder={getText('msg_b7ffb9789f09')}
                                    value={asset.name}
                                    disabled={isAssetLibraryReadOnly}
                                />

                                {isResourceLibrary && (
                                    <div className={styles.resourceScopeField}>
                                        <div className="file-form-label">
                                            {getText('msg_4651a34e4df9')}
                                            <span
                                                className={
                                                    styles.requiredAsterisk
                                                }
                                            >
                                                *
                                            </span>
                                        </div>
                                        <div
                                            className={
                                                styles.resourceScopeOptions
                                            }
                                        >
                                            <SearchableDropdown
                                                placeholderLabel={getText(
                                                    'msg_3e12847dbf80',
                                                )}
                                                options={resourceScopeOptions}
                                                optionIDField="id"
                                                savedOptions={
                                                    selectedResourceScopes
                                                }
                                                savedOptionIDField="id"
                                                updateOptions={
                                                    updateResourceScopes
                                                }
                                                disabled={
                                                    isResourceLibraryReadOnly
                                                }
                                                showTags={false}
                                            />
                                            <div
                                                className={
                                                    styles.resourceScopeTagBox
                                                }
                                            >
                                                {selectedResourceScopes.map(
                                                    scope => (
                                                        <button
                                                            key={scope.id}
                                                            type="button"
                                                            className={`${styles.resourceScopeBadge} ${scope.className}`}
                                                            onClick={() =>
                                                                updateResourceScopes(
                                                                    selectedResourceScopes.filter(
                                                                        selectedScope =>
                                                                            selectedScope.id !==
                                                                            scope.id,
                                                                    ),
                                                                )
                                                            }
                                                            disabled={
                                                                isResourceLibraryReadOnly
                                                            }
                                                        >
                                                            <i className="bi bi-x me-1" />
                                                            {scope.name}
                                                        </button>
                                                    ),
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <TextAreaField
                                    label={
                                        isResourceLibrary
                                            ? getText('msg_1910e1df6652')
                                            : getText('msg_1910e1df6652')
                                    }
                                    onChange={value =>
                                        setAsset(
                                            updateAsset(
                                                asset,
                                                'description',
                                                value,
                                            ),
                                        )
                                    }
                                    maxChars={200}
                                    tooltip={getText('msg_73f1aadae1ad')}
                                    placeholder={getText('msg_3ae3bf00ff41')}
                                    value={asset.description}
                                    disabled={isAssetLibraryReadOnly}
                                />
                            </>
                        )}

                        {isResourceLibrary && (
                            <div
                                className={classNames('mb-4', {
                                    'd-none': activeResourceTab !== 'overview',
                                })}
                            >
                                <div className="file-form-label">
                                    {getText('msg_0efc2e6be4c2')}
                                </div>
                                <p className={styles.resourceHelperText}>
                                    {getText('msg_c7442e9a2cb5')}
                                </p>
                                <RichTextEditor
                                    initialContent=""
                                    onContentUpdate={
                                        handleOverviewContentUpdate
                                    }
                                    disableSSO={true}
                                    disablePortalLink={true}
                                    disableAssetLink={true}
                                    disableHomeLink={true}
                                />
                            </div>
                        )}

                        {isResourceLibrary && isResourceTranscriptRelevant && (
                            <div
                                className={classNames('mb-4', {
                                    'd-none':
                                        activeResourceTab !== 'transcript',
                                })}
                            >
                                <div className="file-form-label">
                                    {getText('msg_5bcd602254f3')}
                                </div>
                                <p className={styles.resourceHelperText}>
                                    {getText('msg_fb24565f5b76')}
                                </p>
                                <RichTextEditor
                                    initialContent=""
                                    onContentUpdate={
                                        handleTranscriptContentUpdate
                                    }
                                    disableSSO={true}
                                    disablePortalLink={true}
                                    disableAssetLink={true}
                                    disableHomeLink={true}
                                />
                            </div>
                        )}

                        {(!isResourceLibrary ||
                            activeResourceTab === 'general') && (
                            <div className={styles.sectionMarginBottom24}>
                                <div className="file-form-label">
                                    {getText('msg_848eed0fbd54')}
                                    <Tooltip
                                        html={getText('msg_79449ece0a91')}
                                        placement="right"
                                    >
                                        <i
                                            className={`bi bi-info-circle ${styles.infoIcon}`}
                                        ></i>
                                    </Tooltip>
                                </div>
                                <TagInput
                                    onTagsChange={tags =>
                                        setAsset(
                                            updateAsset(
                                                asset,
                                                'assetTags',
                                                tags,
                                            ),
                                        )
                                    }
                                    savedTags={asset.tags}
                                    disabled={isAssetLibraryReadOnly}
                                />
                            </div>
                        )}
                        {!isResourceLibrary &&
                            !isMyAssets &&
                            !isGlobalAssets && (
                                <>
                                    <SwitchField
                                        label={getText('msg_0c359a10ae17')}
                                        onChange={isChecked =>
                                            setAsset(
                                                updateAsset(
                                                    asset,
                                                    'assetTypeId',
                                                    isChecked ? 1 : 2,
                                                ),
                                            )
                                        }
                                        tooltip={getText('msg_77453f9dd036')}
                                        checked={asset.assetTypeId === 1}
                                        disabled={isAssetLibraryReadOnly}
                                    />
                                    <CheckboxField
                                        label={getText('msg_a798882f1c31')}
                                        onChange={isChecked =>
                                            setAsset(
                                                updateAsset(
                                                    asset,
                                                    'isLocked',
                                                    isChecked,
                                                ),
                                            )
                                        }
                                        iconRight={
                                            <i
                                                className={`bi bi-lock-fill ${styles.lockIcon}`}
                                            />
                                        }
                                        checked={asset.isLocked}
                                        disabled={isAssetLibraryReadOnly}
                                    />
                                </>
                            )}

                        {!isResourceLibrary &&
                            !isGlobalAssets &&
                            (!isMyAssets || allowAssetSharing) && (
                                <>
                                    <hr className={styles.hrDividerThick} />
                                    <div className={styles.marginBottom68}>
                                        <div className="file-form-label">
                                            {getText('msg_8d1e670cccad')}
                                            <Tooltip
                                                html={
                                                    isMyAssets
                                                        ? getText(
                                                              'msg_219ec8bfd4cd',
                                                          )
                                                        : getText(
                                                              'msg_51b0ed11a997',
                                                          )
                                                }
                                                placement="right"
                                            >
                                                <i
                                                    className={`bi bi-info-circle ${styles.infoIcon}`}
                                                ></i>
                                            </Tooltip>
                                        </div>
                                        <SearchableDropdown
                                            placeholderLabel={getText(
                                                'msg_80edab446ee5',
                                            )}
                                            options={individualsDropdownArray}
                                            optionIDField="id"
                                            savedOptions={
                                                asset.assetIndividuals || []
                                            }
                                            savedOptionIDField="id"
                                            updateOptions={selectedOptions => {
                                                setAsset(
                                                    updateAsset(
                                                        asset,
                                                        'assetIndividuals',
                                                        selectedOptions,
                                                    ),
                                                );
                                            }}
                                            disabled={isAssetLibraryReadOnly}
                                            showTags={true}
                                        />
                                        {!isMyAssets && (
                                            <div className={styles.marginTop20}>
                                                <SearchableDropdown
                                                    placeholderLabel={getText(
                                                        'msg_67d89dcae069',
                                                    )}
                                                    options={
                                                        popGroupsDropdownArray
                                                    }
                                                    optionIDField="id"
                                                    savedOptions={
                                                        asset.assetGroups || []
                                                    }
                                                    savedOptionIDField="id"
                                                    updateOptions={selectedOptions => {
                                                        setAsset(
                                                            updateAsset(
                                                                asset,
                                                                'assetGroups',
                                                                selectedOptions,
                                                            ),
                                                        );
                                                    }}
                                                    disabled={
                                                        isAssetLibraryReadOnly
                                                    }
                                                    showTags={true}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}

                        <div className={styles.marginTop38}>
                            <span className={styles.requiredAsteriskLegend}>
                                *
                            </span>
                            <span className={styles.requiredLegendText}>
                                {getText('msg_04148d61b0b7')}
                            </span>
                        </div>
                        {isMobile ? (
                            <>
                                <div
                                    onClick={() => uploadAsset()}
                                    type="button"
                                    className={classNames(
                                        'button-base ing-button-primary-left',
                                        styles.submitButtonBase,
                                        styles.submitButtonMobile,
                                        {
                                            [styles.submitButtonReadOnly]:
                                                isAssetLibraryReadOnly,
                                        },
                                    )}
                                >
                                    {getText('msg_8bdf057f91e7')}
                                </div>
                            </>
                        ) : (
                            <div
                                onClick={() => uploadAsset()}
                                type="button"
                                className={classNames(
                                    'float-end button-base ing-button-primary-left',
                                    styles.submitButtonBase,
                                    styles.submitButtonDesktop,
                                    {
                                        [styles.submitButtonReadOnly]:
                                            isAssetLibraryReadOnly,
                                    },
                                )}
                            >
                                {getText('msg_8bdf057f91e7')}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NewAsset;
